import { defineStore } from 'pinia'
import {
  STATE_ORDER,
  isActive,
  isSettled,
  type ActivityTask,
  type ActivityKind,
  type ActivityState,
} from '~~/shared/activity/types'

/**
 * The Activity Center store — https://app.notion.com/p/3c304e9a59288185892ef46b9483144f §4.
 *
 * Owns the task list and its lifecycle. **Owns no domain logic whatsoever.**
 *
 * Producers PUSH into it: `useDocumentsIndexingStore` keeps its own batched poller, its own state machine and
 * its own quota parsing, and maps its jobs to `ActivityTask`s. Polling deliberately stays with the
 * producer — indexing polls every 3 s, a workflow may use SSE, research may be push — because
 * centralising transport here would force every future kind through one mechanism, which is the most
 * likely way this design rots.
 *
 * If you find yourself writing the word "quota", "docproc" or "item" in this file, the contract in
 * `shared/activity/types.ts` was too narrow. Widen the contract; do not special-case here.
 */

/** Handlers for `kind: 'run'` actions, keyed `<taskId>:<actionId>`, registered by the producer. */
type RunHandler = () => void | Promise<void>

export const useActivityStore = defineStore('activity', () => {
  const tasks = ref<ActivityTask[]>([])
  const open = ref(false)
  const handlers = new Map<string, RunHandler>()

  /**
   * Whether acknowledged rows are shown. Off by default — the whole point of dismissing is that the
   * row goes away — but dismissal is durable now, so without a way back a mis-click is permanent
   * until someone runs SQL.
   */
  const showDismissed = ref(false)
  let refreshRequested: (() => void) | null = null

  /** The shared feed registers here, so a toggle re-fetches immediately instead of waiting a tick. */
  function onRefreshRequested(fn: () => void) {
    refreshRequested = fn
  }

  function toggleDismissed() {
    showDismissed.value = !showDismissed.value
    refreshRequested?.()
  }

  /** Sorted for display: attention first, then by most recent within each state (§4). */
  const visible = computed(() =>
    [...tasks.value].sort((a, b) =>
      STATE_ORDER[a.state] - STATE_ORDER[b.state] || b.startedAt - a.startedAt))

  /**
   * The counts deliberately EXCLUDE dismissed rows.
   *
   * They are visible only because the user asked to see them, and an acknowledged row must never put
   * the bell back into "1 necesita atención" — that would make dismissing pointless the moment the
   * toggle is on, and the collapsed line is a claim about outstanding work.
   */
  const counted = computed(() => tasks.value.filter(t => !t.dismissed))

  const attention = computed(() => counted.value.filter(t => t.state === 'attention'))
  const active = computed(() => counted.value.filter(t => isActive(t.state)))
  const settled = computed(() => counted.value.filter(t => isSettled(t.state)))

  /** The trigger is hidden entirely when this is false — that is what makes "zero footprint" true. */
  const hasWork = computed(() => counted.value.length > 0)

  /**
   * The collapsed line, as data rather than a string: the panel localizes it.
   * A decision to make outranks progress, which outranks a finished-work receipt.
   */
  const summary = computed<{ tone: 'attention' | 'working' | 'done'; count: number }>(() => {
    if (attention.value.length) return { tone: 'attention', count: attention.value.length }
    if (active.value.length) return { tone: 'working', count: active.value.length }
    return { tone: 'done', count: settled.value.length }
  })

  /**
   * Insert or update a task in place.
   *
   * Update-in-place (rather than remove + push) keeps the row from jumping while a user is reading it,
   * and preserves `startedAt` so the sort stays stable across a poll tick.
   */
  function upsert(task: ActivityTask, runHandlers?: Record<string, RunHandler>) {
    const existing = tasks.value.find(t => t.id === task.id)
    if (existing) Object.assign(existing, task)
    else tasks.value.push(task)

    for (const [actionId, fn] of Object.entries(runHandlers ?? {})) {
      handlers.set(`${task.id}:${actionId}`, fn)
    }
  }

  /** Bulk reconcile for one kind — producers that own a whole list (a poller) call this each tick. */
  function replaceKind(kind: ActivityKind, next: ActivityTask[], runHandlers?: Record<string, RunHandler>) {
    const others = tasks.value.filter(t => t.kind !== kind)
    const byId = new Map(tasks.value.map(t => [t.id, t]))
    // Reuse the existing object where we can, for the same in-place reasons as `upsert`.
    const merged = next.map((t) => {
      const existing = byId.get(t.id)
      if (!existing) return t
      Object.assign(existing, t)
      return existing
    })
    tasks.value = [...others, ...merged]
    for (const [key, fn] of Object.entries(runHandlers ?? {})) handlers.set(key, fn)
  }

  function setState(id: string, state: ActivityState) {
    const task = tasks.value.find(t => t.id === id)
    if (!task) return
    task.state = state
    if (isSettled(state)) task.settledAt = Date.now()
  }

  /** Acknowledge one row. Producers may also call this when their own state disappears. */
  function dismiss(id: string) {
    tasks.value = tasks.value.filter(t => t.id !== id)
    for (const key of handlers.keys()) {
      if (key.startsWith(`${id}:`)) handlers.delete(key)
    }
  }

  /**
   * Clear the receipts, keep anything still in flight or waiting on a decision.
   *
   * Goes through the PRODUCER's own `dismiss` action rather than the in-memory `dismiss()` above
   * (#98). Acknowledgement is durable — a column on each kind's table, written by
   * `POST /api/activity/dismiss` — and the producers already own that call for their per-row
   * "Descartar". Removing the row here and nothing else just let the next poll re-adopt it from the
   * same durable state, so "Limpiar" undid itself: on the very next tick for workflows, and on the
   * next reload for a kind whose poller had already disarmed itself.
   *
   * The store still learns no domain logic: it invokes the handler the producer registered, exactly
   * as the row's own button does. A row whose producer registered none is session-memory with
   * nothing to acknowledge, so removing it from the array IS the whole dismissal.
   */
  async function clearSettled() {
    const receipts = tasks.value.filter(t => isSettled(t.state) && !t.dismissed)
    await Promise.all(receipts.map((task) => {
      const handler = handlers.get(`${task.id}:dismiss`)
      if (!handler) {
        dismiss(task.id)
        return undefined
      }
      return handler()
    }))
  }

  /** Invoke a producer-owned action. Unknown ids are ignored — a stale row must never throw. */
  async function run(taskId: string, actionId: string) {
    await handlers.get(`${taskId}:${actionId}`)?.()
  }

  function toggle() { open.value = !open.value }
  function close() { open.value = false }

  /**
   * Force the panel open — for the rare thing a user MUST see rather than discover.
   *
   * Deliberately not called on ordinary progress: the trigger is quiet chrome by design (§3), and a
   * panel that flew open on every upload would be worse than the silence it replaced. The one case
   * that earns it is work that finished by NOT happening — a refused upload produces no new document,
   * no error and no toast, so nothing on screen changes and the user is left guessing.
   */
  function reveal() { open.value = true }

  return {
    tasks, open, visible, attention, active, settled, hasWork, summary,
    showDismissed, toggleDismissed, onRefreshRequested,
    upsert, replaceKind, setState, dismiss, clearSettled, run, toggle, close, reveal,
  }
})
